import React from 'react';
import PropTypes from 'prop-types';
import { Card, Badge } from '../ui';

/**
 * CourseCard Component
 * Displays a course in a card format
 */
export const CourseCard = ({ 
  course, 
  templateCount = 0,
  activeSessionCount = 0,
  onClick,
  onEdit,
  onDelete
}) => {
  return (
    <Card 
      hover 
      onClick={() => onClick && onClick(course)}
      className="relative"
    >
      <div className="flex items-start gap-4">
        {/* Course Icon */}
        <div
          className="flex items-center justify-center w-16 h-16 rounded-lg text-3xl flex-shrink-0"
          style={{ backgroundColor: course.color }}
        >
          {course.icon}
        </div>

        {/* Course Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-lg text-gray-900 truncate">
              {course.name}
            </h3>
            
            {/* Status Badge */}
            {course.archived && (
              <Badge variant="default" size="sm">Archived</Badge>
            )}
          </div>

          {course.description && (
            <p className="text-sm text-gray-600 line-clamp-2 mt-1">
              {course.description}
            </p>
          )}

          {/* Stats */}
          <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              📄 {templateCount} {templateCount === 1 ? 'template' : 'templates'}
            </span>
            {activeSessionCount > 0 && (
              <span className="flex items-center gap-1">
                🔴 {activeSessionCount} active
              </span>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        {(onEdit || onDelete) && (
          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
            {onEdit && (
              <button
                onClick={() => onEdit(course)}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded theme-transition"
                title="Edit course"
              >
                ✏️
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => onDelete(course)}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded theme-transition"
                title="Delete course"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

CourseCard.propTypes = {
  course: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    color: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    archived: PropTypes.bool
  }).isRequired,
  templateCount: PropTypes.number,
  activeSessionCount: PropTypes.number,
  onClick: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func
};

export default CourseCard;
